import { useState } from 'react'
import { useIncompleteDataStore } from '../store/useIncompleteDataStore'
import { useFillQueueStore } from '../store/useFillQueueStore'
import { useFillAttemptStore, attemptKey } from '../store/useFillAttemptStore'
import { useAuthStore } from '../store/useAuthStore'
import { useStationStore } from '../store/stationStore'
import { useTsInfo } from '../hooks/useTsInfo'
import { getFillApplier } from '../hooks/shared/useToFill'
import { tsLabel } from '../utils/tsLabel'
import { toFillPeriod } from '../utils/toFillPeriod'
import { useStationInlineLabel } from '../utils/stationLabel'

// Štítek „Data nejsou úplná" v pravém dolním rohu (viz doc/plans/fill-attempt-cache.md,
// Fáze 3). Zobrazí se, když uživatel odmítl dotažení / má režim 'off' / běží cache
// pauza — a data některé navštívené obrazovky jsou neúplná. Po odchodu ze stránky
// se její řady z registru smažou, takže v praxi drží jen aktuální stránku.
//
// Skrytý když: běží fronta dotahování (FillProgress má přednost) / uživatel nemá
// právo dotahovat (canFill=false) / žádná neúplná řada.

export function IncompleteDataBadge() {
  const canFill = useAuthStore((s) => s.canFill)
  const active = useFillQueueStore((s) => s.active)
  const byScreen = useIncompleteDataStore((s) => s.byScreen)
  const { infoFor } = useTsInfo()
  const stations = useStationStore((s) => s.allStations)
  const stationLabel = useStationInlineLabel()
  const [open, setOpen] = useState(false)

  // Všechny neúplné řady napříč obrazovkami, dedup podle attemptKey.
  const seen = new Set()
  const list = Object.values(byScreen).flat().filter((e) => {
    if (seen.has(e.key)) return false
    seen.add(e.key)
    return true
  })

  if (!canFill || active || list.length === 0) return null

  // Stanici u řádku ukázat jen když je v nabídce víc stanic (skupinový pohled).
  // U jedné stanice je jasné z kontextu stránky.
  const ghIds = [...new Set(list.map((e) => e.item?.params?.gh_id).filter(Boolean))]
  const showStation = ghIds.length > 1

  const rows = list.map((e) => {
    const info = infoFor(e.item)
    const ghId = e.item?.params?.gh_id
    return {
      label: tsLabel(e.item, info),
      period: toFillPeriod(e.item, info),
      station: showStation ? stationLabel(stations?.find((s) => s.gh_id === ghId), ghId) : null,
    }
  })
  const previewRows = rows.slice(0, 8)
  const more = rows.length - previewRows.length

  const fetchNow = () => {
    const { invalidate, record } = useFillAttemptStore.getState()
    const enqueue = useFillQueueStore.getState().enqueue
    const remove = useIncompleteDataStore.getState().remove
    // seskup řady podle jejich obrazovky (job.key = screenKey)
    const byKey = {}
    for (const [screenKey, entries] of Object.entries(byScreen))
      for (const e of entries) (byKey[screenKey] ??= []).push(e)

    for (const [screenKey, entries] of Object.entries(byKey)) {
      const retryCtx = entries[0]?.retryCtx
      entries.forEach((e) => invalidate(e.key))
      enqueue({
        key: screenKey,
        jobs: [{
          toFill: entries.map((e) => e.item),
          retryCtx,
          onSettled: (item, resp, filled) => {
            const k = attemptKey(item)
            record(k, retryCtx)          // pauza T — nezkoušet hned znovu
            remove(screenKey, k)         // pryč ze štítku
            // tabulka: merge řady z fill odpovědi + úklid toFill (bez refetche celé query);
            // graf aplikátor nemá — přenačte se přes useChartFillBust
            getFillApplier(screenKey)?.(item, resp, filled)
          },
        }],
      })
    }
    setOpen(false)
  }

  return (
    <div className={`fixed bottom-4 right-4 z-[9999] rounded-lg border border-amber-300 bg-white shadow-lg text-sm ${open ? 'w-[26rem] max-w-[calc(100vw-2rem)]' : 'w-64'}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span className="text-amber-500">⚠</span>
        <span className="flex-1 truncate text-gray-700">Data nejsou úplná</span>
        <span className="text-xs text-gray-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-3 py-2">
          <ul className="text-xs space-y-1">
            {previewRows.map((r, i) => (
              <li key={i} className="min-w-0">
                <div className="truncate text-gray-700" title={r.label}>{r.label}</div>
                <div className="truncate text-gray-400" title={[r.period, r.station].filter(Boolean).join(' · ')}>
                  {[r.period, r.station].filter(Boolean).join(' · ')}
                </div>
              </li>
            ))}
            {more > 0 && <li className="text-gray-400">a další {more}…</li>}
          </ul>
          <button
            type="button"
            onClick={fetchNow}
            className="mt-2 w-full rounded bg-blue-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors"
          >
            Dotáhnout
          </button>
          <p className="mt-1 text-[11px] text-gray-400">Dotáhne se v rámci vašich práv.</p>
        </div>
      )}
    </div>
  )
}
